import { auth, firestore, functions } from "./Init";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updatePassword,
  signInWithPopup,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { createUserDocument } from "./User";

// Branded confirmation + reset email is sent by Cloud Functions (Resend), NOT
// the Firebase client SDK — those callables mint the action link server-side and
// deliver the censor-bar HTML template. See functions/emailFunctions/sendAuthEmail.js.
// The old client-side sendEmailVerification()/sendPasswordResetEmail() calls are
// intentionally gone; wiring these callables is what actually triggers the emails.

// Create user with email/password
export const doCreateUserWithEmailAndPassword = async (email, password) => {
  try {
    let userCredential;
    try {
      userCredential = await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Error creating Firebase Auth user:", error);
      throw error;
    }

    const user = userCredential.user;

    try {
      await createUserDocument(user, {
        email: user.email,
      });
    } catch (error) {
      // Deliberately NOT deleting the auth account here.
      //
      // This used to `await user.delete()`. Destroying a just-created account
      // over a transient Firestore blip is worse than the orphan it prevents —
      // and if the delete ITSELF failed, the user was left in exactly the bricked
      // state we were trying to avoid (auth account, no doc, infinite spinner).
      //
      // The doc is now recoverable three ways: the onAuthUserCreate trigger
      // provisions it server-side within ~1s, backfillMissingUserDocs repairs
      // historical orphans, and UserDocRecovery self-heals on the next visit.
      // Signup proceeds; the account is usable either way.
      console.error("Error creating user document (recoverable, not fatal):", error);
    }

    // Fire the branded confirmation email. Non-fatal + fire-and-forget: the
    // account already exists, and signup navigates away immediately, so a mail
    // failure (or cold start) must not block or fail the flow. The callable
    // reads the address from the freshly-minted auth token, not from client data.
    httpsCallable(functions, "sendVerificationEmail")().catch((verifyErr) => {
      console.error("Confirmation email dispatch failed (non-fatal):", verifyErr);
    });

    return userCredential;
  } catch (error) {
    console.error("Full error in doCreateUserWithEmailAndPassword:", {
      code: error.code,
      message: error.message,
      stack: error.stack,
      name: error.name,
      fullError: error,
    });
    throw error;
  }
};

// Sign in with email/password.
//
// Deliberately auth-only: no Firestore write happens here. A `lastSignInTime`
// mirror used to run on this path, but the field was never in the firestore.rules
// users-update allowlist, so every write was permission-denied, the helper
// rethrew, and this function rejected AFTER a successful sign-in — SignIn then
// rendered an error on a good login. Firebase Auth already tracks this as
// user.metadata.lastSignInTime (and lastLoginAt/lastRefreshAt via the Admin SDK),
// so the mirror was pure liability. Nothing post-auth may throw here.
export const doSignInWithEmailAndPassword = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    console.error("Error in signInWithEmailAndPassword:", error);
    throw error;
  }
};

// Sign in with Google (popup). Matches the dev-branch flow that's known to
// work end-to-end. signInWithPopup resolves with the user when the OAuth
// flow completes, and onAuthStateChanged fires immediately.
export const doSignInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    const userRef = doc(firestore, "users", user.uid);
    const snapshot = await getDoc(userRef);

    // Returning users need no write. The `lastSignInTime` mirror that used to
    // run in an else-branch here was rejected by firestore.rules on every call,
    // which threw before the token refresh below and meant getIdToken(true)
    // never ran for any returning Google user.
    if (!snapshot.exists()) {
      await createUserDocument(user, {
        email: user.email,
        display_name: user.displayName,
      });
    }

    await user.getIdToken(true);
    return { user };
  } catch (error) {
    console.error("Error during Google sign-in:", error);
    throw error;
  }
};

// Basic auth operations
export const doSignOut = () => auth.signOut();

// Reset + verification go through the branded Resend callables. The reset
// callable is unauthenticated by design (forgot-password flow) and always
// resolves success to avoid account enumeration; verification requires the
// caller to be signed in (address comes from the verified token).
export const doPasswordReset = (email) =>
  httpsCallable(functions, "sendPasswordResetEmail")({ email });

export const doSendEmailVerification = () =>
  httpsCallable(functions, "sendVerificationEmail")();

export const doPasswordChange = (password) => updatePassword(auth.currentUser, password);

// Account deletion. The client no longer deletes anything — it reauthenticates,
// calls the server, and signs out.
//
// ── WHY THE OLD SHAPE WAS BROKEN ────────────────────────────────────────────
//
// It ran `deleteDoc(users/{uid})` and then `deleteUser(user)`, untransacted,
// and only reauthenticated PASSWORD users who happened to supply a password. A
// Google-provider account, or an email account with a stale session, therefore
// hit `auth/requires-recent-login` on the second call — AFTER the user doc was
// already gone. That left the exact bricked state backfillMissingUserDocs.js
// exists to repair: auth record alive, no user doc, AuthGate treating the
// missing doc as "still loading" and spinning forever. The member saw an error
// and their account was now unusable.
//
// The server deletes via the Admin SDK, which has no recency requirement, so
// that whole failure window stops existing. Reauthentication stays, but as
// proof of presence rather than as a precondition for the delete — and the
// server enforces it for EVERY provider via auth_time, which is the first time
// Google users have had any gate at all on an irreversible action.
//
// It also does far more than before: cancels the subscription (fail-closed),
// withdraws every portrait from every deck, and queues the full erasure. See
// functions/accountFunctions/deleteAccount.js.
export const doDeleteAccount = async (password = null) => {
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently signed in.");

  const isEmailUser = user.providerData.some(
    (provider) => provider.providerId === "password"
  );

  if (isEmailUser && password) {
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
  }

  const deleteAccount = httpsCallable(functions, "deleteAccount");
  const res = await deleteAccount({});
  if (res?.data?.status !== "success") {
    const err = new Error(res?.data?.error_message || "Deletion failed.");
    err.code = res?.data?.error_code || "INTERNAL";
    throw err;
  }

  // Sign out only after the server confirms. Doing it first would drop the
  // token the callable needs; doing it never leaves a signed-in session whose
  // auth record no longer exists, which is what lets UserDocRecovery try to
  // recreate the account three seconds later.
  await doSignOut();
};
